<script setup>
import axios from 'axios';
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import PgeButton from '../../../../pge-components/pge-button.vue';
import { useAuthStore } from '../../../../stores/auth';
import { clearPendingVerification, rememberPendingVerification } from '../../../../helpers/pendingVerification';

const props = defineProps({
	eventData: Object,
	changeFormStep: Function,
	currentFormStepData: {
		type: Object,
		default: () => {},
	},
});

const code = ref(['', '', '', '', '', '']);
const inputRefs = ref([]);
const error = ref('');
const loading = ref(false);
const resendLoading = ref(false);
const resendMessage = ref('');
const router = useRouter();
const authStore = useAuthStore();

const email = props.currentFormStepData?.email ?? '';

onMounted(() => {
	// The account exists and is unverified from here on, so record it: if this
	// window is closed, coming back to /register resumes at this step instead of
	// dead-ending on a password prompt with no way to request a new code.
	rememberPendingVerification(email);
	inputRefs.value[0]?.focus();
});

function useDifferentEmail() {
	clearPendingVerification();
	props.changeFormStep('check-email');
}

function handleChange(index, value) {
	if (value && !/^\d$/.test(value)) return;

	code.value[index] = value;
	error.value = '';

	if (value && index < 5) {
		inputRefs.value[index + 1]?.focus();
	}
}

function handleKeyDown(index, e) {
	if (e.key === 'Backspace' && !code.value[index] && index > 0) {
		inputRefs.value[index - 1]?.focus();
	}
}

function handlePaste(e) {
	e.preventDefault();
	const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
	if (pasted.length === 0) return;

	const newCode = Array(6).fill('');
	for (let i = 0; i < 6; i++) {
		newCode[i] = pasted[i] || '';
	}
	code.value = newCode;

	const focusIndex = Math.min(pasted.length, 5);
	inputRefs.value[focusIndex]?.focus();
}

async function handleSubmit() {
	const fullCode = code.value.join('');

	if (fullCode.length !== 6) {
		error.value = 'Please enter the full 6-digit code.';
		return;
	}

	loading.value = true;
	error.value = '';

	try {
		const res = await axios.post('/api/sso/api/email/verify-code', {
			email,
			code: fullCode,
		});

		if (res.data?.success) {
			clearPendingVerification();

			const token = res.data?.access_token;
			if (token) {
				axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
				await authStore.retrieveUserData();

				const expiresAt = res.data?.expires_at;
				let expiresInDays = 1;
				if (expiresAt) {
					const expireMs = new Date(expiresAt).getTime() - Date.now();
					if (expireMs > 0) {
						expiresInDays = Math.max(1, Math.ceil(expireMs / (1000 * 60 * 60 * 24)));
					}
				}

				authStore.login({ access_token: token, expires_in: expiresInDays });
				router.push('/');
				return;
			}

			props.changeFormStep('success-message', props.currentFormStepData);
		} else {
			error.value = res.data?.message || 'Invalid verification code.';
			code.value = Array(6).fill('');
			inputRefs.value[0]?.focus();
		}
	} catch (err) {
		const message = err.response?.data?.message;
		error.value = message || 'Something went wrong. Please try again.';
		code.value = Array(6).fill('');
		inputRefs.value[0]?.focus();
	} finally {
		loading.value = false;
	}
}

async function handleResend() {
	if (!email) return;
	resendLoading.value = true;
	resendMessage.value = '';
	error.value = '';

	try {
		const res = await axios.post('/api/sso/api/email/resend-code', {
			email,
			subdomain: window.store?.subdomain,
			logo: props.eventData?.login_logo,
		});

		if (res.status < 300) {
			resendMessage.value = 'A new code has been sent to your email.';
			code.value = Array(6).fill('');
			inputRefs.value[0]?.focus();
		} else {
			error.value = res.data?.message || 'Failed to resend code.';
		}
	} catch (err) {
		error.value = err.response?.data?.message || 'Something went wrong. Please try again.';
	} finally {
		resendLoading.value = false;
	}
}
</script>

<template>
	<div class="verify-code">
		<div class="verify-code__title">
			Enter Verification Code
		</div>
		<div class="verify-code__description">
			We sent a 6-digit verification code to
			<strong>{{ email || 'your email' }}</strong>.
			Enter it below to verify your account.
		</div>
		<div class="verify-code__inputs" @paste="handlePaste">
			<input
				v-for="(digit, index) in code"
				:key="index"
				:ref="(el) => (inputRefs[index] = el)"
				type="text"
				inputmode="numeric"
				maxlength="1"
				:value="digit"
				:aria-label="`Verification code digit ${index + 1}`"
				class="verify-code__input"
				@input="handleChange(index, $event.target.value)"
				@keydown="handleKeyDown(index, $event)"
				:autofocus="index === 0"
			/>
		</div>
		<div class="verify-code__error" v-if="error">{{ error }}</div>
		<div class="verify-code__resend-message" v-if="resendMessage">{{ resendMessage }}</div>
		<div class="verify-code__actions">
			<pge-button
				label="Resend Code"
				template="text"
				:disabled="resendLoading"
				@click="handleResend"
			/>
			<pge-button
				:label="loading ? 'Verifying...' : 'Verify'"
				:disabled="loading || code.join('').length !== 6"
				@click="handleSubmit"
			/>
		</div>
		<button type="button" class="verify-code__restart" @click="useDifferentEmail">
			Use a different email
		</button>
	</div>
</template>

<style lang="scss" scoped>
.verify-code {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 22px;

	&__title {
		color: #000;
		font-feature-settings: 'liga' off, 'clig' off;
		font-family: Lato, sans-serif;
		font-size: 16px;
		font-style: normal;
		font-weight: 700;
		line-height: normal;
		text-align: center;
	}

	&__description {
		font-size: 14px;
		color: #49454f;
		text-align: center;
		line-height: 1.5;
	}

	&__inputs {
		display: flex;
		gap: 8px;
		justify-content: center;
	}

	&__input {
		width: 44px;
		height: 52px;
		text-align: center;
		font-size: 22px;
		font-weight: 600;
		border: 1px solid #9b9b9b;
		border-radius: 5px;
		outline: none;
		font-family: monospace;

		&:focus {
			border-color: #583174;
			box-shadow: 0 0 0 2px rgba(88, 49, 116, 0.2);
		}
	}

	&__error {
		color: #f83c3c;
		font-weight: 700;
		font-size: 14px;
		text-align: center;
	}

	&__resend-message {
		color: #3d66be;
		font-size: 14px;
		text-align: center;
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	&__restart {
		border: 0;
		background: transparent;
		padding: 0;
		cursor: pointer;
		font-size: 14px;
		color: #3d66be;
		text-decoration: underline;
	}
}
</style>
